import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { forwardRef } from 'react';

import { Button, Icon, IconButton } from '@travelnest/hui';

import { Panel } from '@/components/ui/Panel';
import { Stack } from '@/components/ui/Stack';
import { Heading, Text } from '@/components/ui/Text';
import { colors, mq, radii, spacing } from '@/lib/theme';
import { useI18n } from '@/locales';
import type { IProperty } from '@/types/content';

// Google Maps only runs in the browser.
const GoogleMap = dynamic(
	() => import('./GoogleMap').then((m) => m.GoogleMap),
	{
		ssr: false,
	},
);

const MapPanel = styled(Panel)`
	position: relative;
	overflow: hidden;
	min-height: 280px;

	${mq.lg} {
		min-height: 420px;
	}
`;

/** Stands in for the map without a Google Maps key (local runs, tests). */
export const MapPlaceholder = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: ${spacing.space30};
	background:
		radial-gradient(circle at 30% 30%, ${colors.BB20}, transparent 60%),
		radial-gradient(circle at 70% 70%, ${colors.GR20}, transparent 55%),
		${colors.LM_NU20};
	color: ${colors.LM_NU70};

	svg {
		color: ${colors.BB100};
	}
`;

const Controls = styled.div`
	position: absolute;
	right: ${spacing.space40};
	bottom: ${spacing.space40};
	display: flex;
	flex-direction: column;
	gap: ${spacing.space20};

	button {
		box-shadow: ${colors.LM_NU30T} 0 2px 8px;
		border-radius: ${radii.radii50};
	}
`;

export const googleMapsSearchUrl = (
	property: Pick<IProperty, 'city' | 'postcode' | 'country'>,
) =>
	encodeURI(
		`https://www.google.com/maps/search/${property.city}, ${property.postcode}, ${property.country}`,
	);

interface LocationMapProps {
	property: IProperty;
}

/**
 * "Where you'll be": the town and country, and a map centred on the property.
 * Without a Google Maps key (local runs, tests) a styled placeholder with a
 * link out to Google Maps takes its place.
 */
export const LocationMap = forwardRef<HTMLDivElement, LocationMapProps>(
	function LocationMap({ property }, ref) {
		const t = useI18n();
		const hasKey = Boolean(process.env.NEXT_PUBLIC_GOOGLE_KEY);
		const mapsUrl = googleMapsSearchUrl(property);

		return (
			<Stack ref={ref} gap="space30" id="location">
				<Heading as="h2" size="md">
					{t('property.location.title')}
				</Heading>
				<Text>
					{property.city}, {property.country}
				</Text>
				<MapPanel padding="none" outlined data-testid="map">
					{hasKey ? (
						<GoogleMap
							coordinates={[
								property.latitude,
								property.longitude,
							]}
							renderControls={(map) => (
								<Controls>
									<IconButton
										icon="Plus"
										variant="white"
										aria-label={t(
											'property.location.zoomIn',
										)}
										onClick={() =>
											map.setZoom(
												(map.getZoom() ?? 12) + 1,
											)
										}
									/>
									<IconButton
										icon="Minus"
										variant="white"
										aria-label={t(
											'property.location.zoomOut',
										)}
										onClick={() =>
											map.setZoom(
												(map.getZoom() ?? 12) - 1,
											)
										}
									/>
									<IconButton
										icon="PinOnMap"
										variant="white"
										aria-label={t(
											'property.location.openInMaps',
										)}
										onClick={() =>
											window.open(
												mapsUrl,
												'_blank',
												'noopener',
											)
										}
									/>
								</Controls>
							)}
						/>
					) : (
						<MapPlaceholder>
							<Icon name="PinOnMap" size={48} />
							<Button
								text={t('property.location.openInMaps')}
								variant="white"
								size="small"
								onClick={() =>
									window.open(mapsUrl, '_blank', 'noopener')
								}
							/>
						</MapPlaceholder>
					)}
				</MapPanel>
			</Stack>
		);
	},
);
